import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SiTourService, TourStep } from '@siemens/element-ng/tour';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';

import { Schedule } from '../DataTypes';
import { mockVehicle } from '../mockdata/mock-vehicle';
import { AccountService } from './account.service';
import { EventService } from './event.service';

interface TourConfig {
  id: string;
  route: string;
  autoStart: boolean;
  priority: number;
  steps: TourStep[];
  oncePerSession?: boolean;
}

interface TourCompletionStatus {
  tourId: string;
  completed: boolean;
  skipped: boolean;
  completedAt?: string;
  startCount: number;
}

interface TourState {
  activeTourId: string | null;
  isRunning: boolean;
}

type TourEventType = 'started' | 'ended' | 'skipped' | 'completed';

interface TourEvent {
  type: TourEventType;
  tourId: string;
  timestamp: Date;
}

/** Guided-tour manager wrapping SiTourService. Persists skip/complete state to localStorage. */
@Injectable({
  providedIn: 'root'
})
export class TourService implements OnDestroy {
  private readonly siTourService = inject(SiTourService);
  private readonly router = inject(Router);
  private readonly accountService = inject(AccountService);

  private readonly destroy$ = new Subject<void>();
  private readonly registeredTours = new Map<string, TourConfig>();
  private readonly sessionStartedTours = new Set<string>();

  private readonly tourState$ = new BehaviorSubject<TourState>({
    activeTourId: null,
    isRunning: false
  });

  private readonly tourEvents$ = new Subject<TourEvent>();
  private readonly storageKeyPrefix = 'fast-track-tour-completion-status';
  private readonly eventService = inject(EventService);
  private readonly pendingWaits = new Set<() => void>();
  private preparationGeneration = 0;
  private autoStartTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  constructor() {
    this.siTourService.setOptions({
      // `scrollTo` is the documented scroll hook: the tour scrolls the anchor into view after
      // `beforeShowPromise` resolves and then measures it synchronously for the highlight.
      // `behavior: 'instant'` is required because the Element theme sets
      // `html { scroll-behavior: smooth }`, which would make the default `auto` animate and
      // leave the highlight at a stale offset.
      defaultStepOptions: {
        scrollTo: { behavior: 'instant', block: 'center', inline: 'nearest' }
      }
    });
    this.setupTourEventListeners();
  }

  ngOnDestroy(): void {
    this.cancelPendingPreparations();
    this.closeTransientOverlaysImmediately();
    this.clearTourData();
    if (this.tourState$.value.isRunning) {
      this.resetState();
      this.siTourService.complete();
    }
    if (this.autoStartTimer) {
      clearTimeout(this.autoStartTimer);
      this.autoStartTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.registerAllTours();
    this.subscribeToRouteChanges();
    this.handleRouteChange(this.router.url);
  }

  startTour(tourId: string, force = false): void {
    const tourConfig = this.registeredTours.get(tourId);
    if (!tourConfig) {
      return;
    }
    if (!force && this.isDismissed(tourId)) {
      return;
    }
    if (this.tourState$.value.isRunning) {
      return;
    }

    this.cancelPendingPreparations();
    this.closeTransientOverlaysImmediately();
    this.siTourService.clearSteps();
    this.siTourService.addSteps(tourConfig.steps);

    this.tourState$.next({ activeTourId: tourId, isRunning: true });
    this.incrementStartCount(tourId);
    this.sessionStartedTours.add(tourId);
    this.emitTourEvent('started', tourId);

    this.siTourService.start();
  }

  endTour(markCompleted = false): void {
    const { isRunning, activeTourId } = this.tourState$.value;
    if (!isRunning || !activeTourId) {
      return;
    }

    if (markCompleted) {
      this.markCompleted(activeTourId);
      this.emitTourEvent('completed', activeTourId);
    } else {
      this.emitTourEvent('ended', activeTourId);
    }

    this.cancelPendingPreparations();
    this.closeTransientOverlaysImmediately();
    this.clearTourData();
    this.resetState();
    this.siTourService.complete();
  }

  resetTour(tourId: string): void {
    this.resetStatus(tourId);
    this.sessionStartedTours.delete(tourId);
  }

  resetAllTours(): void {
    this.resetAllStatuses();
    this.sessionStartedTours.clear();
  }

  isTourCompleted(tourId: string): boolean {
    return this.getStatus(tourId)?.completed ?? false;
  }

  getTourState() {
    return this.tourState$.asObservable();
  }

  getTourEvents() {
    return this.tourEvents$.asObservable();
  }

  isRunning(): boolean {
    return this.tourState$.value.isRunning;
  }

  private registerAllTours(): void {
    for (const tour of this.getAllTours()) {
      this.registeredTours.set(tour.id, tour);
    }
  }

  private getAllTours(): TourConfig[] {
    return [this.getOnboardingTour()];
  }

  private getOnboardingTour(): TourConfig {
    return {
      id: 'onboarding',
      route: '/main/configuration',
      autoStart: true,
      priority: 1,
      oncePerSession: true,
      steps: this.getOnboardingSteps()
    };
  }

  private waitForElement(
    selector: string,
    timeoutMs = 8000,
    pollIntervalMs = 50
  ): Promise<HTMLElement | null> {
    return this.waitForCondition(
      () => !!document.querySelector(selector),
      timeoutMs,
      pollIntervalMs
    ).then(found => (found ? (document.querySelector(selector) as HTMLElement | null) : null));
  }

  private waitForElementToDisappear(
    selector: string,
    timeoutMs = 4000,
    pollIntervalMs = 50
  ): Promise<boolean> {
    return this.waitForCondition(
      () => !document.querySelector(selector),
      timeoutMs,
      pollIntervalMs
    );
  }

  private waitForCondition(
    condition: () => boolean,
    timeoutMs: number,
    pollIntervalMs: number
  ): Promise<boolean> {
    const generation = this.preparationGeneration;

    return new Promise(resolve => {
      const start = Date.now();
      let timer: ReturnType<typeof setTimeout> | undefined;
      let finished = false;

      const finish = (result: boolean): void => {
        if (finished) {
          return;
        }
        finished = true;
        if (timer) {
          clearTimeout(timer);
        }
        this.pendingWaits.delete(cancel);
        resolve(result);
      };

      const cancel = (): void => finish(false);

      this.pendingWaits.add(cancel);

      const check = (): void => {
        if (generation !== this.preparationGeneration) {
          finish(false);
          return;
        }
        if (condition()) {
          finish(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          finish(false);
          return;
        }
        timer = setTimeout(check, pollIntervalMs);
      };

      check();
    });
  }

  private cancelPendingPreparations(): void {
    this.preparationGeneration += 1;
    for (const cancel of Array.from(this.pendingWaits)) {
      cancel();
    }
  }

  private waitForNextPaint = async (frameCount = 2): Promise<void> => {
    const generation = this.preparationGeneration;
    for (let index = 0; index < frameCount; index += 1) {
      if (generation !== this.preparationGeneration) {
        return;
      }
      await new Promise<void>(resolve => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(resolve, 0);
        }
      });
    }
  };

  /**
   * Runs the optional data preparation and waits until the target exists and the layout settled.
   * Scrolling is intentionally left to the tour itself via the `scrollTo` step option.
   */
  private prepareStep = (
    selector: string,
    prepare?: () => Promise<void>
  ): (() => Promise<void>) => {
    return async () => {
      await prepare?.();
      if (!(await this.waitForElement(selector))) {
        return;
      }
      await this.waitForNextPaint(3);
    };
  };

  private clickElement = async (selector: string, timeoutMs = 8000): Promise<boolean> => {
    const element = await this.waitForElement(selector, timeoutMs);
    if (!element || element.hasAttribute('disabled')) {
      return false;
    }

    element.click();
    await this.waitForNextPaint();
    return true;
  };

  private ensureMapModalOpen = async (): Promise<void> => {
    if (!document.querySelector('[data-tour-id="tour-map-close"]')) {
      if (!(await this.clickElement('[data-tour-id="tour-map-button"]'))) {
        return;
      }
      await this.waitForElement('[data-tour-id="tour-map-close"]');
    }

    if (!(await this.waitForElement('[data-tour-id="tour-map-planner"]'))) {
      return;
    }
    this.eventService.loadTourMapRoute(
      { id: 19016, stationName: 'Erlangen', lat: 49.5960006, lon: 11.0017075 },
      {
        id: 29791,
        stationName: 'Munich Central Station',
        lat: 48.1407253,
        lon: 11.5569426
      }
    );
    await this.waitForNextPaint(3);
  };

  private closeMapModal = async (): Promise<void> => {
    if (!document.querySelector('[data-tour-id="tour-map-close"]')) {
      return;
    }

    await this.clickElement('[data-tour-id="tour-map-close"]');
    await this.waitForElementToDisappear('[data-tour-id="tour-map-close"]');
    await this.waitForNextPaint();
  };

  private loadTourVehicleData = async (): Promise<void> => {
    this.eventService.loadTourVehicle(mockVehicle);
    await this.waitForElement('[data-tour-id="tour-vehicle"]');
    await this.waitForNextPaint();
  };

  private loadTourTrackData = async (): Promise<void> => {
    const tourTrackData = await import('../mockdata/tour-testtrack.json');
    this.eventService.loadTourTrack(tourTrackData.default ?? tourTrackData);
    await this.waitForElement('[data-tour-id="tour-track-parameter-select"]');
    await this.waitForNextPaint();
  };

  /**
   * The plot button stays disabled for station/GPS data and for parameters without rows, so
   * pick the first parameter that actually enables it instead of the first non-station option.
   */
  private selectPlottableTrackParameter = async (): Promise<void> => {
    const select = (await this.waitForElement(
      '[data-tour-id="tour-track-parameter-select"]'
    )) as HTMLSelectElement | null;
    const plotButton = await this.waitForElement('[data-tour-id="tour-track-plot-button"]');
    if (!select || !plotButton) {
      return;
    }

    if (!plotButton.hasAttribute('disabled')) {
      return;
    }

    const originalValue = select.value;
    const candidates = Array.from(select.options)
      .map(option => option.value)
      .filter(value => value !== 'station_data' && value !== 'gps_data');

    for (const value of candidates) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      await this.waitForNextPaint();
      if (!plotButton.hasAttribute('disabled')) {
        return;
      }
    }

    select.value = originalValue;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    await this.waitForNextPaint();
  };

  private loadTourScheduleData = async (): Promise<void> => {
    const tourScheduleData = await import('../mockdata/mock-schedule.json');
    this.eventService.loadTourSchedule((tourScheduleData.default ?? tourScheduleData) as Schedule);
    await this.waitForElement('[data-tour-id="tour-schedule-table"]');
    await this.waitForNextPaint();
  };

  private closeTransientOverlaysImmediately(): void {
    const mapClose = document.querySelector(
      '[data-tour-id="tour-map-close"]'
    ) as HTMLElement | null;
    mapClose?.click();
  }

  private clearTourData(): void {
    this.eventService.clearTourData();
  }

  private prepareOverview = async (): Promise<void> => {
    this.closeTransientOverlaysImmediately();
    this.clearTourData();
    await this.waitForNextPaint();
  };

  private prepareFinish = async (): Promise<void> => {
    this.closeTransientOverlaysImmediately();
    await this.waitForNextPaint();
  };

  private getOnboardingSteps(): TourStep[] {
    return [
      {
        id: 'overview',
        title: 'TOUR.ONBOARDING.OVERVIEW.TITLE',
        text: 'TOUR.ONBOARDING.OVERVIEW.CONTENT',
        beforeShowPromise: this.prepareOverview
      },
      {
        attachTo: { element: '[data-tour-id="tour-vehicle"]' },
        id: 'vehicle',
        title: 'TOUR.ONBOARDING.VEHICLE.TITLE',
        text: 'TOUR.ONBOARDING.VEHICLE.CONTENT',
        beforeShowPromise: this.prepareStep(
          '[data-tour-id="tour-vehicle"]',
          this.loadTourVehicleData
        )
      },
      {
        attachTo: { element: '[data-tour-id="tour-track-load-import"]' },
        id: 'track-load-import',
        title: 'TOUR.ONBOARDING.TRACK_LOAD.TITLE',
        text: 'TOUR.ONBOARDING.TRACK_LOAD.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-track-load-import"]')
      },
      {
        attachTo: { element: '[data-tour-id="tour-map-button"]' },
        id: 'map',
        title: 'TOUR.ONBOARDING.MAP.TITLE',
        text: 'TOUR.ONBOARDING.MAP.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-map-button"]', this.closeMapModal)
      },
      {
        attachTo: { element: '[data-tour-id="tour-map-planner"]' },
        id: 'map-planner',
        title: 'TOUR.ONBOARDING.MAP_PLANNER.TITLE',
        text: 'TOUR.ONBOARDING.MAP_PLANNER.CONTENT',
        beforeShowPromise: this.ensureMapModalOpen
      },
      {
        attachTo: { element: '[data-tour-id="tour-map-brouter"]' },
        id: 'map-brouter',
        title: 'TOUR.ONBOARDING.MAP_BROUTER.TITLE',
        text: 'TOUR.ONBOARDING.MAP_BROUTER.CONTENT',
        beforeShowPromise: this.ensureMapModalOpen,
        beforeNextPromise: this.closeMapModal
      },
      {
        attachTo: { element: '[data-tour-id="tour-track-table"]' },
        id: 'track-data',
        title: 'TOUR.ONBOARDING.TRACK_DATA.TITLE',
        text: 'TOUR.ONBOARDING.TRACK_DATA.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-track-table"]', async () => {
          await this.closeMapModal();
          await this.loadTourTrackData();
        })
      },
      {
        attachTo: { element: '[data-tour-id="tour-track-plot-button"]' },
        id: 'track-plot',
        title: 'TOUR.ONBOARDING.PLOT.TITLE',
        text: 'TOUR.ONBOARDING.PLOT.CONTENT',
        beforeShowPromise: this.prepareStep(
          '[data-tour-id="tour-track-plot-button"]',
          this.selectPlottableTrackParameter
        )
      },
      {
        attachTo: { element: '[data-tour-id="tour-schedule-load"]' },
        id: 'schedule-load',
        title: 'TOUR.ONBOARDING.SCHEDULE_LOAD.TITLE',
        text: 'TOUR.ONBOARDING.SCHEDULE_LOAD.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-schedule-load"]')
      },
      {
        attachTo: { element: '[data-tour-id="tour-schedule-table"]' },
        id: 'schedule-table',
        title: 'TOUR.ONBOARDING.SCHEDULE_TABLE.TITLE',
        text: 'TOUR.ONBOARDING.SCHEDULE_TABLE.CONTENT',
        beforeShowPromise: this.prepareStep(
          '[data-tour-id="tour-schedule-table"]',
          this.loadTourScheduleData
        )
      },
      {
        attachTo: { element: '[data-tour-id="tour-simulate"]' },
        id: 'simulate',
        title: 'TOUR.ONBOARDING.SIMULATE.TITLE',
        text: 'TOUR.ONBOARDING.SIMULATE.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-simulate"]')
      },
      {
        attachTo: { element: '[data-tour-id="tour-results"]' },
        id: 'results',
        title: 'TOUR.ONBOARDING.RESULTS.TITLE',
        text: 'TOUR.ONBOARDING.RESULTS.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-results"]')
      },
      {
        attachTo: { element: '[data-tour-id="tour-workspaces"]' },
        id: 'workspaces',
        title: 'TOUR.ONBOARDING.WORKSPACES.TITLE',
        text: 'TOUR.ONBOARDING.WORKSPACES.CONTENT',
        beforeShowPromise: this.prepareStep('[data-tour-id="tour-workspaces"]')
      },
      {
        id: 'finish',
        title: 'TOUR.ONBOARDING.FINISH.TITLE',
        text: 'TOUR.ONBOARDING.FINISH.CONTENT',
        beforeShowPromise: this.prepareFinish
      }
    ];
  }

  private setupTourEventListeners(): void {
    this.siTourService.onTourComplete.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const { isRunning, activeTourId } = this.tourState$.value;
      if (isRunning && activeTourId) {
        this.markCompleted(activeTourId);
        this.emitTourEvent('completed', activeTourId);
        this.cancelPendingPreparations();
        this.closeTransientOverlaysImmediately();
        this.clearTourData();
        this.resetState();
      }
    });

    this.siTourService.onTourCancel.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const { isRunning, activeTourId } = this.tourState$.value;
      if (isRunning && activeTourId) {
        this.markSkipped(activeTourId);
        this.emitTourEvent('skipped', activeTourId);
        this.cancelPendingPreparations();
        this.closeTransientOverlaysImmediately();
        this.clearTourData();
        this.resetState();
      }
    });
  }

  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => this.handleRouteChange(event.urlAfterRedirects));
  }

  private handleRouteChange(url: string): void {
    if (this.tourState$.value.isRunning) {
      if (!url.includes('/main/configuration')) {
        this.cancelPendingPreparations();
        this.closeTransientOverlaysImmediately();
        this.clearTourData();
        this.resetState();
        this.siTourService.complete();
      }
      return;
    }

    if (this.autoStartTimer) {
      clearTimeout(this.autoStartTimer);
      this.autoStartTimer = null;
    }

    const matchingTours = Array.from(this.registeredTours.values())
      .filter(tour => {
        const routeMatches = url.includes(tour.route) || tour.route === '*';
        const notDismissed = tour.autoStart && !this.isDismissed(tour.id);
        const notStartedThisSession =
          !tour.oncePerSession || !this.sessionStartedTours.has(tour.id);
        return routeMatches && notDismissed && notStartedThisSession;
      })
      .sort((a, b) => a.priority - b.priority);

    if (matchingTours.length > 0) {
      const tourId = matchingTours[0].id;
      this.autoStartTimer = setTimeout(() => {
        this.autoStartTimer = null;
        if (this.router.url.includes(matchingTours[0].route)) {
          this.startTour(tourId);
        }
      }, 500);
    }
  }

  private isDismissed(tourId: string): boolean {
    const status = this.getStatus(tourId);
    return status?.completed === true || status?.skipped === true;
  }

  private markCompleted(tourId: string): void {
    this.updateStatus(tourId, {
      completed: true,
      skipped: false,
      completedAt: new Date().toISOString()
    });
  }

  private markSkipped(tourId: string): void {
    if (!this.getStatus(tourId)?.completed) {
      this.updateStatus(tourId, { completed: false, skipped: true });
    }
  }

  private incrementStartCount(tourId: string): void {
    const status = this.getStatus(tourId);
    const count = (status?.startCount ?? 0) + 1;
    this.updateStatus(tourId, { startCount: count });
  }

  private resetStatus(tourId: string): void {
    const statuses = this.getAllStatuses().filter(s => s.tourId !== tourId);
    this.saveStatuses(statuses);
  }

  private resetAllStatuses(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // The in-memory session guard still prevents repeated automatic starts.
    }
  }

  private getStatus(tourId: string): TourCompletionStatus | undefined {
    return this.getAllStatuses().find(s => s.tourId === tourId);
  }

  private updateStatus(tourId: string, updates: Partial<TourCompletionStatus>): void {
    const statuses = this.getAllStatuses();
    const index = statuses.findIndex(s => s.tourId === tourId);
    const existing =
      index >= 0 ? statuses[index] : { tourId, completed: false, skipped: false, startCount: 0 };
    const updated = { ...existing, ...updates };

    if (index >= 0) {
      statuses[index] = updated;
    } else {
      statuses.push(updated);
    }
    this.saveStatuses(statuses);
  }

  private getAllStatuses(): TourCompletionStatus[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? (JSON.parse(stored) as TourCompletionStatus[]) : [];
    } catch {
      return [];
    }
  }

  private saveStatuses(statuses: TourCompletionStatus[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(statuses));
    } catch {
      // Tour availability must not depend on browser storage being writable.
    }
  }

  private resetState(): void {
    this.tourState$.next({ activeTourId: null, isRunning: false });
  }

  private emitTourEvent(type: TourEvent['type'], tourId: string): void {
    this.tourEvents$.next({ type, tourId, timestamp: new Date() });
  }

  private get storageKey(): string {
    const account = this.accountService.getActiveAccount();
    const accountId = account?.homeAccountId ?? account?.username ?? 'anonymous';
    return `${this.storageKeyPrefix}:${accountId}`;
  }
}
